import {
  CREATE_ALERT_BEGIN,
  CREATE_ALERT_SUCCESS,
  CREATE_ALERT_FAILURE,
  CREATE_ALERT_DISMISS_ERROR,
  CREATE_ALERTS_BATCH_BEGIN,
  CREATE_ALERTS_BATCH_SUCCESS,
  CREATE_ALERTS_BATCH_FAILURE,
} from './constants';
import { fetchAlerts } from './fetchAlerts';
import { app } from '../../../common/feathers';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useCallback } from 'react';
import { calculateNextDate } from '../timeAlertUtils';
import { TIME_ALERT_PARAM } from '../alertConstants';

const alertService = app.service('alerts');

// Helper function to prepare alert data with fresh nextDate for time-based alerts
const prepareAlertData = (alert) => {
  // Recalculate nextDate for time-based alerts
  if (alert.param === TIME_ALERT_PARAM && alert.data) {
    const freshNextDate = calculateNextDate(
      alert.data.startDate,
      alert.data.everyXDays,
      alert.data.recurring
    );

    return {
      ...alert,
      data: {
        ...alert.data,
        nextDate: freshNextDate
      }
    };
  }

  return alert;
};

// Helper function to create a single alert
const createSingleAlert = async (args, dispatch) => {
  try {
    const res = await alertService.create(args);
    dispatch({
      type: CREATE_ALERT_SUCCESS,
      data: res,
    });
    return res;
  } catch (err) {
    dispatch({
      type: CREATE_ALERT_FAILURE,
      data: {
        error: err,
        id: args.id
      },
    });
    throw err;
  }
};

// Main function that handles both single and batch alert creation
export function createAlert(args) {
  return async (dispatch) => {
    const userId = app.get('user')._id;
    
    // Handle batch alert creation
    if (Array.isArray(args)) {
      // Only track alerts with IDs
      const alertIds = args.filter(alert => alert.id).map(alert => alert.id);
      
      dispatch({
        type: CREATE_ALERTS_BATCH_BEGIN,
        data: alertIds
      });

      try {
        // Create alerts sequentially to maintain order and prevent race conditions
        const results = [];
        for (const alert of args) {
          const preparedAlert = prepareAlertData(alert);
          const alertWithUserId = { ...preparedAlert, userId };
          const result = await createSingleAlert(alertWithUserId, dispatch);
          results.push(result);
        }

        dispatch({
          type: CREATE_ALERTS_BATCH_SUCCESS,
          data: results,
        });

        // Fetch updated alerts after batch creation
        await fetchAlerts({ userId })(dispatch);
        return results;

      } catch (err) {
        dispatch({
          type: CREATE_ALERTS_BATCH_FAILURE,
          data: {
            error: err,
            ids: alertIds
          },
        });
        throw err;
      }
    } else {
      // Handle single alert creation
      dispatch({
        type: CREATE_ALERT_BEGIN,
        data: args.id || null
      });

      try {
        const preparedAlert = prepareAlertData(args);
        const alertWithUserId = { ...preparedAlert, userId };
        const result = await createSingleAlert(alertWithUserId, dispatch);
        await fetchAlerts({ userId })(dispatch);
        return result;
      } catch (err) {
        // Error is already dispatched in createSingleAlert
        throw err;
      }
    }
  };
}

export const useCreateAlert = () => {
  const dispatch = useDispatch();
  const {
    createAlertPending,
    createAlertError,
    createAlertsInProgress,
    createAlertsFailed,
    batchCreatePending,
  } = useSelector(
    state => ({
      createAlertPending: state.alert.createAlertPending,
      createAlertError: state.alert.createAlertError,
      createAlertsInProgress: state.alert.createAlertsInProgress,
      createAlertsFailed: state.alert.createAlertsFailed,
      batchCreatePending: state.alert.batchCreatePending,
    }),
    shallowEqual,
  );

  const boundAction = useCallback(
    data => dispatch(createAlert(data)),
    [dispatch],
  );

  const boundDismissError = useCallback(
    () => dispatch(dismissCreateAlertError()),
    [dispatch],
  );

  return {
    createAlert: boundAction,
    createAlertPending,
    createAlertError,
    createAlertsInProgress,
    createAlertsFailed,
    batchCreatePending,
    dismissCreateAlertError: boundDismissError,
  };
};

export function dismissCreateAlertError() {
  return {
    type: CREATE_ALERT_DISMISS_ERROR,
  };
}

export function reducer(state, action) {
  switch (action.type) {
    case CREATE_ALERTS_BATCH_BEGIN:
      return {
        ...state,
        batchCreatePending: true,
        createAlertError: null,
        createAlertsInProgress: action.data ? [...state.createAlertsInProgress, ...action.data] : state.createAlertsInProgress,
      };

    case CREATE_ALERTS_BATCH_SUCCESS:
      return {
        ...state,
        batchCreatePending: false,
        createAlertError: null,
        createAlertsInProgress: state.createAlertsInProgress.filter(
          id => !action.data.find(alert => alert.id === id)
        ),
        createAlertsFailed: state.createAlertsFailed.filter(
          id => !action.data.find(alert => alert.id === id)
        ),
      };

    case CREATE_ALERTS_BATCH_FAILURE:
      return {
        ...state,
        batchCreatePending: false,
        createAlertError: action.data.error,
        createAlertsFailed: [...state.createAlertsFailed, ...action.data.ids],
      };

    case CREATE_ALERT_BEGIN:
      return {
        ...state,
        createAlertPending: true,
        createAlertError: null,
        createAlertsInProgress: action.data && action.data !== null
          ? [...state.createAlertsInProgress, action.data]
          : state.createAlertsInProgress,
      };

    case CREATE_ALERT_SUCCESS:
      return {
        ...state,
        createAlertPending: false,
        createAlertError: null,
        alerts: [...(state.alerts || []), action.data],
        createAlertsInProgress: action.data.id != null
          ? state.createAlertsInProgress.filter(id => id !== action.data.id)
          : state.createAlertsInProgress,
        createAlertsFailed: action.data.id != null
          ? state.createAlertsFailed.filter(id => id !== action.data.id)
          : state.createAlertsFailed,
      };

    case CREATE_ALERT_FAILURE:
      return {
        ...state,
        createAlertPending: false,
        createAlertError: action.data.error,
        createAlertsFailed: action.data.id != null
          ? [...state.createAlertsFailed, action.data.id]
          : state.createAlertsFailed,
      };

    case CREATE_ALERT_DISMISS_ERROR:
      return {
        ...state,
        createAlertError: null,
      };

    default:
      return state;
  }
}